import React, { JSX } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  MenuList,
  Tab,
  Tabs
} from "@mui/material";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { push } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import IpdBills from "./IpdBills";
import IpdDetails from "./IpdDetails";
import { RootState } from "../../store";
import Panel from "../../components/Panel";
import { IpdStatus } from "../../constants/ipd";
import { t, tl } from "../../components/translate";
import { CanActions } from "../../interfaces/policy";
import * as ipdClientActions from "../../actions/ipd";
import IpdMedication from "./IpdProgress/IpdMedication";
import IpdVitalsshow from "./IpdProgress/IpdVitalsshow";
import { RequestType } from "./LabRequest/LabRequestForm";
import { postIpdRequestForm } from "../../api/requestForms";
import { notificationAdd } from "../../actions/notification";
import { DischargeContent } from "./Discharge/DischargeDetails";
import IpdMedicationShow from "./IpdProgress/IpdMedicationShow";
import { Medicine } from "../../interfaces/AssessmentInterfaces";
import IpdProductMedication from "./IpdProgress/IpdProductMedication";
import IpdProgressRecordShow from "./IpdProgress/IpdProgressRecordShow";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import RecordRefundPayment from "../Billing/RecordPayment/RecordRefundPayment";
import IpdProgressRecordCreateEdit from "./IpdProgress/IpdProgressRecordCreateEdit";
import { IMedicine, IPD, ExtendedIpd, MedicationSummary } from "../../interfaces/IpdInterface";
import { MEDICINE_COMPONENT } from "../ResourceCentre/ModuleSettings/IpdSettings/GeneralIpdSettings";

const switchTabRenderer = (activeTab: number, ipdRecord: IPD) => {
  switch (activeTab) {
    case 0:
      return <IpdProgressRecordShow ipdRecord={ipdRecord} />;
    case 1:
      return <IpdVitalsshow ipdRecord={ipdRecord} />;
    case 2:
      return <IpdMedicationShow medicineData={ipdRecord.prescribedMedications} />;
    case 3:
      return <IpdBills ipdRecord={ipdRecord} />;
    case 4:
      if (ipdRecord.status === IpdStatus.DISCHARGED) {
        return <DischargeContent ipdData={ipdRecord} />;
      }
      return "";

    default:
      throw new Error(`Unknown activeTab: ${activeTab}`);
  }
};

// eslint-disable-next-line import/prefer-default-export
export function IpdInfoPanel({
  ipd,
  wrapperStyle,
  editButton,
  onClose,
  onDischargeFormOpen,
  onIpdChange
}: {
  ipd: ExtendedIpd;
  wrapperStyle?: React.CSSProperties;
  editButton?: React.ReactNode;
  onClose: () => void;
  onDischargeFormOpen: () => void;
  onIpdChange?: (updatedIpd: IPD) => void;
}): JSX.Element {
  const rc = useCurrentResourceCentre();
  const dispatch = useDispatch();
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // selectors
  const policies = useSelector(
    (state: RootState) => state.userContext.policies || {}
  ) as Partial<CanActions>;

  // local states
  const [openMenu, setOpenMenu] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);
  const [showProgressPanel, setShowProgressPanel] = React.useState(false);
  const [openRecordReceipt, setOpenRecordReceipt] = React.useState(false);
  const [medicationDialogOpen, setMedicationDialogOpen] = React.useState(false);
  const [medication, setMedication] = React.useState([] as Array<IMedicine | Medicine>);

  // variables
  const title = ipd?.status === IpdStatus.ADMITTED ? t("admissionInfo") : t("dischargeInfo");

  // helper functions
  const handleToggle = () => {
    setOpenMenu((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpenMenu(false);
  };

  const createMedicationPrescription = async (ipdId: number, medications: MedicationSummary[]) => {
    await dispatch(ipdClientActions.createIpdPrescribeMedication(ipdId, medications));
    // create request form for prescribed medications
    const medicationFromStock = medications?.filter((item) => Boolean(item.isFromStock));
    if (medicationFromStock?.length) {
      try {
        await postIpdRequestForm({
          ipdId,
          requestedItems: medications,
          requestedItemsType: RequestType.PRODUCT
        });
      } catch (err: any) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: err.message || "Failed to create request form",
            autoTimeout: true
          })
        );
      }
    }
  };

  return (
    <>
      <Panel
        title={title}
        wrapperStyle={{ minWidth: "300px", width: "100%", ...wrapperStyle }}
        onClose={onClose}
        editButton={editButton}
      >
        <Box>
          <IpdDetails ipd={ipd} />

          <Box>
            {ipd?.status === IpdStatus.ADMITTED && (
              <ButtonGroup
                size="small"
                variant="outlined"
                color="primary"
                ref={anchorRef}
                aria-label="split button"
              >
                <Button
                  data-testmation="createProgressRecord"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setShowProgressPanel(true);
                  }}
                  disabled={!policies["ipd:createProgressRecord"]}
                >
                  Progress Record
                </Button>
                <Button
                  data-testmation="clientinfoDropdown"
                  size="small"
                  color="primary"
                  aria-owns={openMenu ? "menu-list-grow" : undefined}
                  aria-haspopup="true"
                  onClick={handleToggle}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
            )}

            <Popper
              style={{ zIndex: 1 }}
              open={openMenu}
              anchorEl={anchorRef.current}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === "bottom" ? "right bottom" : "right bottom"
                  }}
                >
                  <Paper id="menu-list-grow">
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList>
                        <MenuItem
                          data-testmation="ipdMedication"
                          onClick={() => {
                            setOpenMenu(false);
                            setMedicationDialogOpen(true);
                          }}
                          color="primary"
                          style={{ minWidth: "200px" }}
                          disabled={!policies["ipd:prescribeMedication"]}
                        >
                          Prescribe Medicine
                        </MenuItem>

                        <MenuItem
                          data-testmation="recordReceipt"
                          onClick={() => {
                            setOpenMenu(false);
                            setOpenRecordReceipt(true);
                          }}
                          color="primary"
                          style={{ minWidth: "200px" }}
                        >
                          {tl("client.recordReceipt")}
                        </MenuItem>

                        <MenuItem
                          data-testmation="ipdDischarge"
                          onClick={() => {
                            setOpenMenu(false);
                            onDischargeFormOpen();
                          }}
                          color="primary"
                          style={{ minWidth: "200px" }}
                          disabled={!policies["ipd:createDischarge"]}
                        >
                          Discharge
                        </MenuItem>
                        <MenuItem
                          data-testmation="createBill"
                          onClick={() => {
                            if (ipd?.clientId && ipd?.bedId) {
                              dispatch(
                                push(
                                  `/billing/ipd?clientId=${ipd.clientId}&bedId=${ipd.bedId}&ipdId=${ipd.id}`
                                )
                              );
                            }
                          }}
                          color="primary"
                          style={{ minWidth: "200px" }}
                        >
                          Create IPD Bill
                        </MenuItem>
                        <MenuItem
                          data-testmation="createProformaBill"
                          onClick={() => {
                            if (ipd?.clientId && ipd?.bedId) {
                              dispatch(
                                push(
                                  `/billing/ipd?clientId=${ipd.clientId}&bedId=${ipd.bedId}&ipdId=${ipd.id}&isProformaBill=true`
                                )
                              );
                            }
                          }}
                          color="primary"
                          style={{ minWidth: "200px" }}
                        >
                          Create Proforma Bill
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>

          {/* tabs */}
          <Box marginTop="32px">
            <Tabs
              value={activeTab}
              onChange={(e, tab) => setActiveTab(tab)}
              aria-label="ipd client info tabs"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Progress Record" />
              <Tab label="Vitals" />
              <Tab label="Prescribed Medicine" />
              <Tab label="Bills" />
              {ipd?.status === IpdStatus.DISCHARGED && <Tab label="DischargeSummary" />}
            </Tabs>
            {switchTabRenderer(activeTab, ipd)}
          </Box>
        </Box>
      </Panel>

      {/* panels */}
      <>
        {showProgressPanel && ipd && (
          <IpdProgressRecordCreateEdit
            onCancel={() => setShowProgressPanel(false)}
            ipdRecord={ipd}
            afterSave={(updatedIpd: IPD) => {
              onIpdChange?.(updatedIpd);
            }}
          />
        )}

        {openRecordReceipt && (ipd?.clientId || ipd?.client?.id) && (
          <RecordRefundPayment
            clientId={ipd.clientId || ipd?.client?.id}
            onClose={() => {
              setOpenRecordReceipt(false);
            }}
            refundMode={false}
          />
        )}

        {medicationDialogOpen && (
          <Dialog
            fullWidth
            onClose={() => setMedicationDialogOpen(false)}
            open={medicationDialogOpen}
          >
            <DialogTitle
              style={{
                borderBottom: "1px solid #ececec",
                background: "#f9f9f9",
                textTransform: "uppercase"
              }}
            >
              Medications
            </DialogTitle>
            <DialogContent style={{ padding: "24px 32px" }}>
              <Box style={{ width: "100%", display: "flex" }}>
                {rc.settings.ipdSettings.medicationComponent === MEDICINE_COMPONENT.MEDICATION ? (
                  <IpdMedication
                    id={ipd?.id}
                    medication={medication}
                    setMedication={setMedication}
                  />
                ) : (
                  <IpdProductMedication productMedication={medication} onChange={setMedication} />
                )}
              </Box>
            </DialogContent>
            <DialogActions
              style={{
                background: "#00800014",
                borderTop: "1px solid #00800017",
                justifyContent: "flex-start"
              }}
            >
              <Box
                width="100%"
                display="flex"
                justifyContent="space-between"
                padding="0px 24px"
                alignContent="center"
              >
                <Box />
                <Box>
                  <Button
                    style={{ marginRight: "8px" }}
                    data-testmation="medication.cancelButton"
                    color="primary"
                    onClick={() => {
                      setMedicationDialogOpen(false);
                    }}
                  >
                    cancel
                  </Button>
                  <Button
                    data-testmation="medicationSaveButton"
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      createMedicationPrescription(ipd.id, medication);
                      setMedicationDialogOpen(false);
                    }}
                  >
                    save
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </Dialog>
        )}
      </>
    </>
  );
}
